import { create } from "zustand";

import type { GalleryImage } from "@/react/types/GalleryImage";

import { ASPECT_RATIOS, STEPS } from "./constants";

type StepName = (typeof STEPS)[keyof typeof STEPS] | null;

interface Point {
  x: number;
  y: number;
}

export interface Crop {
  height: number;
  width: number;
  x: number;
  y: number;
}

export interface SelectedImage {
  crop: Point;
  croppedAreaPixels?: Crop;
  file: File;
  preview: string;
  zoom: number;
}

export interface UploadedImage extends Omit<GalleryImage, "id"> {
  id?: number;
}

interface ImagePostModalStore {
  addSelectedFiles: (files: SelectedImage[]) => void;
  addUploadedImages: (files: UploadedImage[]) => void;
  currentStep: StepName;
  galleryAspectRatio: number | null;
  isFormDirty: boolean;
  isImagesDirty: boolean;
  isSubmitting: boolean;
  reset: () => void;
  resetSelectedFiles: () => void;
  selectedFiles: SelectedImage[];
  setCurrentStep: (identifier: StepName) => void;
  setGalleryAspectRatio: (aspectRatio: number) => void;
  setIsFormDirty: (isFormDirty: boolean) => void;
  setIsImagesDirty: (isImagesDirty: boolean) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setSelectedFiles: (files: SelectedImage[]) => void;
  setUploadedImages: (images: UploadedImage[]) => void;
  updateImage: (image: SelectedImage) => void;
  uploadedImages: UploadedImage[];
}

const cleanupSelectedFiles = (
  state: Pick<ImagePostModalStore, "selectedFiles">,
) => {
  state.selectedFiles.forEach(file => URL.revokeObjectURL(file.preview));
  return { selectedFiles: [] };
};

export const useImagePostModalStore = create<ImagePostModalStore>(set => ({
  currentStep: STEPS.UPLOAD_IMAGES,
  isSubmitting: false,
  isFormDirty: false,
  isImagesDirty: false,
  galleryAspectRatio: null,
  selectedFiles: [],
  uploadedImages: [],
  setCurrentStep: identifier => set({ currentStep: identifier }),
  setIsSubmitting: isSubmitting => set({ isSubmitting }),
  setIsFormDirty: isFormDirty => set({ isFormDirty }),
  setIsImagesDirty: isImagesDirty => set({ isImagesDirty }),
  updateImage: image => {
    set(state => {
      const imageIndex = state.selectedFiles.findIndex(
        img => img.preview === image.preview,
      );
      if (imageIndex === -1) return {};

      return {
        selectedFiles: state.selectedFiles.map((selectedFile, index) =>
          index === imageIndex ? image : selectedFile,
        ),
      };
    });
  },
  addSelectedFiles: files => {
    set(state => ({
      selectedFiles: [...state.selectedFiles, ...files],
    }));
  },
  setSelectedFiles: files => {
    set({ selectedFiles: files });
  },
  addUploadedImages: images => {
    set(state => ({
      uploadedImages: [...state.uploadedImages, ...images],
    }));
  },
  setUploadedImages: images => {
    set({ uploadedImages: images });
  },
  setGalleryAspectRatio: aspectRatio => {
    if (aspectRatio < (ASPECT_RATIOS.portrait + ASPECT_RATIOS.square) / 2) {
      set({ galleryAspectRatio: ASPECT_RATIOS.portrait });
    } else if (
      aspectRatio >
      (ASPECT_RATIOS.landscape + ASPECT_RATIOS.square) / 2
    ) {
      set({ galleryAspectRatio: ASPECT_RATIOS.landscape });
    } else {
      set({ galleryAspectRatio: ASPECT_RATIOS.square });
    }
  },
  resetSelectedFiles: () => {
    set(cleanupSelectedFiles);
  },
  reset: () => {
    set(state => {
      const cleanedFiles = cleanupSelectedFiles(state);
      return {
        ...cleanedFiles,
        currentStep: null,
        uploadedImages: [],
        galleryAspectRatio: null,
        isSubmitting: false,
        isFormDirty: false,
        isImagesDirty: false,
      };
    });
  },
}));
